import { useState, useEffect } from "react";
import API from "api/endpoints";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "api/axiosInstance";
// InterviewStatusEnum was deleted (replaced by InterviewRoundStatusEnum on the
// new multi-round flow). Status writes below now use literal values.

export default function InterviewService(jobPostingId) {
  const id = jobPostingId; // Use the parameter instead of useParams
  const [firstDateTime, setFirstDateTime] = useState(null);
  const [secondDateTime, setSecondDateTime] = useState(null);
  const [thirdDateTime, setThirdDateTime] = useState(null);
  const [jobApplicantId, setJobApplicantId] = useState(null);
  const [email, setEmail] = useState(null);
  const [loadingInterviewEmails, setLoadingEmails] = useState({});
  const [interviewScheduled, setInterviewScheduled] = useState({});

  useEffect(() => {
    if (id) {
      // Add this check
      fetchEmployeesByjobPostingId(id);
    }
  }, [id]); // Add id as a dependency

  const fetchEmployeesByjobPostingId = async (id) => {
    try {
      const response = await axiosInstance.get(API.hiringJobApplicant.getListByPosting(id));
      const responseData = response.data.applicants.map((info) => ({
        key: info.jobApplicantId.toString(),
        employeeId: info.jobApplicantId,
        status: info.status,
      }));
      setInterviewScheduled(responseData);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const [open, setOpen] = useState(false);
  const showModal = (jobApplicantId, email) => {
    setOpen(true);
    setJobApplicantId(jobApplicantId);
    setEmail(email);
    setFirstDateTime(null);
    setSecondDateTime(null);
    setThirdDateTime(null);
  };

  const handleOk = () => {
    const currentDate = new Date();

    const firstDate = new Date(firstDateTime).toLocaleDateString();
    const secondDate = new Date(secondDateTime).toLocaleDateString();
    const thirdDate = new Date(thirdDateTime).toLocaleDateString();

    const firstTime = new Date(firstDateTime).toLocaleTimeString();
    const secondTime = new Date(secondDateTime).toLocaleTimeString();
    const thirdTime = new Date(thirdDateTime).toLocaleTimeString();

    if (
      (firstDate === secondDate && firstTime === secondTime) ||
      (secondDate === thirdDate && secondTime === thirdTime) ||
      (firstDate === thirdDate && firstTime === thirdTime)
    ) {
      toast.error("Interview times cannot be the same for the same date.");
      return;
    }

    if (
      new Date(firstDateTime) >= new Date(secondDateTime) ||
      new Date(secondDateTime) >= new Date(thirdDateTime)
    ) {
      toast.error("Interview times must be in chronological order.");
      return;
    }

    if (
      new Date(firstDateTime) < currentDate ||
      new Date(secondDateTime) < currentDate ||
      new Date(thirdDateTime) < currentDate
    ) {
      toast.error("Interview times cannot be before the current date and time.");
      return;
    }

    setOpen(false);
    handleInterviewClick(jobApplicantId, firstDateTime, secondDateTime, thirdDateTime);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleInterviewClick = async (
    jobApplicantId,
    firstDateTime,
    secondDateTime,
    thirdDateTime
  ) => {
    try {
      setLoadingEmails((prev) => ({ ...prev, [email]: true }));

      // Validate jobApplicantId is provided and valid
      if (!jobApplicantId || jobApplicantId === 0) {
        toast.error("Invalid job applicant ID");
        setLoadingEmails((prev) => ({ ...prev, [email]: false }));
        return;
      }

      // Convert to proper Date objects and validate
      const firstDate = new Date(firstDateTime);
      const secondDate = new Date(secondDateTime);
      const thirdDate = new Date(thirdDateTime);

      // Validate dates are valid
      if (isNaN(firstDate) || isNaN(secondDate) || isNaN(thirdDate)) {
        toast.error("Invalid date format provided");
        setLoadingEmails((prev) => ({ ...prev, [email]: false }));
        return;
      }

      // Backend expects only these fields
      const interviewData = {
        jobApplicantId: parseInt(jobApplicantId), // Ensure it's a number
        firstDateTime: firstDate.toISOString(),
        secondDateTime: secondDate.toISOString(),
        thirdDateTime: thirdDate.toISOString(),
      };

      const response = await axiosInstance.post(
        API.hiringInterviews.proposeInterview,
        interviewData,
        {
          timeout: 10000,
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response.status === 200) {
        toast.success("Interview slots proposed successfully");

        // Update the interview scheduled status
        setInterviewScheduled((prev) =>
          prev.map((item) =>
            item.employeeId === jobApplicantId
              ? { ...item, status: 1 /* legacy "scheduled" — old enum was unused */ }
              : item
          )
        );
      }
    } catch (error) {
      console.error("❌ Error handling interview:", error);

      // Enhanced error logging
      console.error("❌ Error details:", {
        message: error.message,
        code: error.code,
        name: error.name,
        config: error.config
          ? {
              url: error.config.url,
              data: error.config.data,
              method: error.config.method,
            }
          : "No config",
      });

      if (error.code === "NETWORK_ERROR" || error.code === "ECONNABORTED") {
        toast.error("Network error. Please check your connection and try again.");
      } else if (error.response) {
        console.error(" Server response error:", error.response.data);
        console.error(" Server response status:", error.response.status);
        toast.error(
          error.response.data?.message ||
            error.response.data?.error ||
            `Server error (${error.response.status}) while scheduling interview.`
        );
      } else if (error.request) {
        console.error(" No response received:", error.request);
        toast.error("No response from server. Please check your connection.");
      } else {
        console.error(" Unexpected error:", error.message);
        toast.error("Failed to schedule interview: " + (error.message || "Unknown error"));
      }
    } finally {
      setLoadingEmails((prev) => ({ ...prev, [email]: false }));
    }
  };

  return {
    jobApplicantId,
    open,
    setOpen,
    email,
    firstDateTime,
    secondDateTime,
    thirdDateTime,
    setFirstDateTime,
    setSecondDateTime,
    setThirdDateTime,
    showModal,
    handleOk,
    handleCancel,
    handleInterviewClick,
    interviewScheduled,
    setInterviewScheduled,
    loadingInterviewEmails,
  };
}
